import { Action } from "@/api-schema-enums";
import { Can } from "@/casl-provider";
import {
  useBrandByUuidQuery,
  type BrandByUuidParameters,
  type BrandByUuidResponse,
} from "@/common/api/brand/use-brand-by-uuid.query";
import { BrandLogo, BrandLogoVariant } from "@/components/brand-logo/brand-logo";
import { createRowNavigatePath } from "@/components/data-table/_helpers/create-row-navigate-path";
import { BranchTable } from "@/components/data-table/predefined/branch-table";
import { UserTable } from "@/components/data-table/predefined/user-table";
import { DetailHeader } from "@/components/detail-page/detail-header";
import { DetailPageLayout } from "@/components/detail-page/detail-page-layout";
import { DetailPageSkeleton } from "@/components/detail-page/detail-page-skeleton";
import { DetailTabs } from "@/components/detail-page/detail-tabs";
import { SystemIdTab, type SystemIdExternalIdentifier } from "@/components/detail-page/tabs/system-id-tab";
import type { DetailAction, DetailTab } from "@/components/detail-page/types";
import { CenteredLayoutVariant } from "@/components/layouts/centered-layout";
import { Badge, BadgeColorVariant, BadgeVariant } from "@/components/ui/badge";
import { UuidExistenceWrapper } from "@/components/uuid-existence-wrapper/uuid-existence-wrapper";
import { renderBrandType } from "@/modules/masterdata/brand/_helpers/type-renderer";
import { CarFront, Fingerprint, PencilIcon, Store, UsersIcon } from "lucide-react";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "wouter";

const BRAND_DETAIL_TRACKING_NAME = "brand";

const RELATED_BRANCH_ROW_ACTIONS = {
  showView: false,
  showEdit: false,
  showDelete: false,
  showRestore: false,
} as const;

export function BrandDetailView(): ReactElement {
  return (
    <Can I="read" a="Brand">
      <BrandDetailQuery />
    </Can>
  );
}

function BrandDetailQuery(): ReactElement {
  const { t } = useTranslation(["masterdata"]);
  const params = useParams<{ uuid: string }>();
  const uuid = params.uuid ?? "";
  const brandQuery = useBrandByUuidQuery(uuid);

  if (brandQuery.isPending) {
    return <DetailPageSkeleton />;
  }

  return (
    <UuidExistenceWrapper<BrandByUuidParameters, BrandByUuidResponse>
      uuid={uuid}
      query={useBrandByUuidQuery}
      queryResult={brandQuery}
      notFoundProps={{
        title: t("masterdata:BrandNotFound"),
        message: t("masterdata:BrandDoesNotExist"),
        description: t("masterdata:CheckBrandIdOrContactSupport"),
        backTo: "~/masterdata/brand",
        backToLabel: t("masterdata:BackToBrandOverview"),
      }}
    >
      {brandQuery.data ? <BrandDetailContent brand={brandQuery.data} /> : null}
    </UuidExistenceWrapper>
  );
}

function BrandDetailContent({ brand }: { brand: BrandByUuidResponse }): ReactElement {
  const { t } = useTranslation(["masterdata", "common"]);
  const [, navigate] = useLocation();
  const isDeleted = Boolean(brand.deletedAt);

  const actions: DetailAction[] = isDeleted
    ? []
    : [
        {
          id: "edit",
          label: t("common:Edit"),
          icon: PencilIcon,
          permission: { action: Action.Manage, subject: "Brand" },
          onClick: () => navigate(`~/masterdata/brand/${brand.uuid}/update`),
        },
      ];

  const externalIdentifiers: SystemIdExternalIdentifier[] = [
    { id: "v1-id", label: t("masterdata:V1ID"), value: brand.v1Id },
    { id: "dat-id", label: t("masterdata:DATID"), value: brand.datId },
  ];

  const tabs: DetailTab[] = [
    {
      id: "branches",
      label: t("common:Branches"),
      icon: Store,
      content: () => (
        <BranchTable
          name="brand-branches"
          body={{ brands: [brand.uuid] }}
          showCreateButton={false}
          defaultRowActions={RELATED_BRANCH_ROW_ACTIONS}
          onRowNavigate={createRowNavigatePath("/masterdata/branch")}
        />
      ),
    },
    {
      id: "users",
      label: t("common:Users"),
      icon: UsersIcon,
      content: () => <UserTable name="brand-users" body={{ brands: [brand.uuid] }} />,
    },
    {
      id: "identifiers",
      label: t("masterdata:SystemIds"),
      icon: Fingerprint,
      content: () => (
        <SystemIdTab
          uuid={brand.uuid}
          technicalId={brand.technicalId}
          createdAt={brand.createdAt}
          updatedAt={brand.updatedAt}
          deletedAt={brand.deletedAt}
          identifiers={externalIdentifiers}
        />
      ),
    },
  ];

  return (
    <DetailPageLayout
      variant={CenteredLayoutVariant.Wide}
      header={
        <DetailHeader
          icon={brand.logoUrl ? <BrandLogo brand={brand} variant={BrandLogoVariant.MEDIUM} /> : <CarFront />}
          title={brand.name}
          subtitle={renderBrandType(brand.type)}
          badges={
            isDeleted ? (
              <Badge variant={BadgeVariant.Outlined} colorVariant={BadgeColorVariant.Error}>
                {t("common:Deleted")}
              </Badge>
            ) : undefined
          }
          actions={actions}
        />
      }
    >
      <DetailTabs sections={tabs} trackingName={BRAND_DETAIL_TRACKING_NAME} />
    </DetailPageLayout>
  );
}
